// @ commons
const { APIGet, APIPost } = require("utilities/APIRequestHelper");
const apiURLBuilder = require("core/apiURLBuilder.js");
const moment = require("moment");

const { formatText } = require("core/i18n").i18n;
const signaturePatternActions = require("actions/signaturePattern");

// @utilities
const { recordLink } = require("utilities/tagging");

// @ constants
const {
    FETCH_SESSIONS_BY_DEVICE_IN_PROGRESS,
    FETCH_SESSIONS_BY_DEVICE_SUCCESS,
    FETCH_SESSIONS_BY_DEVICE_FAILURE,
    FETCH_DEVICE_ACTION_IN_PROGRESS,
    FETCH_DEVICE_ACTION_SUCCESS,
    FETCH_DEVICE_ACTION_ERROR,
    FETCH_SESSION_ACTION_IN_PROGRESS,
    FETCH_SESSION_ACTION_SUCCESS,
    FETCH_SESSION_ACTION_ERROR,
    FETCH_DEVICES_FAILURE,
    FETCH_DEVICES_SUCCESS,
    FETCH_DEVICES_IN_PROGRESS,
    FETCH_DEVICES_CLEAR,
    FETCH_DEVICES_CLEAR_SESSIONS,
    FETCH_DEVICE_ENROLMENT_INFORMATION,
    FETCH_DEVICE_ENROLMENT_IN_PROGRESS,
    FETCH_DEVICE_ENROLMENT_ERROR,
    FETCH_DEVICE_ENROLMENT_SUCCESS,
    FETCH_DEVICE_ENROLMENT_STATE,
    FETCH_DEVICE_ENROLMENT_DATA_TOKEN,
    FETCH_AGENT_PROFILE_VALIDATE_SUCCESS
} = require("constants/actionTypes");
const { DEVICES_TO_SHOW } = require("constants/index");
const {
    DEVICES_BLOCK,
    DEVICES_EDIT_NAME,
    DEVICES_ERROR,
    DEVICES_HIDE,
    DEVICES_SUCCESS,
} = require("constants/tagging.js");

const { success } = require("actions/notifications");
const SESSIONS_LIMIT = 10;

// @ actions
const clearDevicesAction = () => ({
    type: FETCH_DEVICES_CLEAR
});

const removeSessionsByDeviceAction = deviceId => ({
    type: FETCH_DEVICES_CLEAR_SESSIONS,
    payload: {
        deviceId
    }
});

const openModal = data => ({
    type: data.type
});

const requestDevicesInProgress = (blocked, forgotten, isFetching) => ({
    type: FETCH_DEVICES_IN_PROGRESS,
    payload: {
        blocked,
        forgotten,
        isFetching
    }
});

const requestDevicesSuccess = (blocked, forgotten, devices) => ({
    type: FETCH_DEVICES_SUCCESS,
    payload: {
        blocked,
        forgotten,
        devices
    }
});

const requestDevicesFailure = (error, forgotten, blocked) => ({
    type: FETCH_DEVICES_FAILURE,
    payload: {
        blocked,
        forgotten,
        error
    }
});

const requestSessionsByDeviceInProgress = deviceId => ({
    type: FETCH_SESSIONS_BY_DEVICE_IN_PROGRESS,
    payload: {
        deviceId
    }
});

const requestSessionsByDeviceSuccess = (deviceId, sessions) => ({
    type: FETCH_SESSIONS_BY_DEVICE_SUCCESS,
    payload: {
        deviceId,
        sessions
    }
});

const requestSessionsByDeviceFailure = (error, deviceId) => ({
    type: FETCH_SESSIONS_BY_DEVICE_FAILURE,
    payload: {
        deviceId,
        error
    }
});

const requestDeviceActionInProgress = deviceId => ({
    type: FETCH_DEVICE_ACTION_IN_PROGRESS,
    payload: {
        deviceId
    }
});

const requestDeviceActionSuccess = (deviceId, ccActionCode = "") => ({
    type: FETCH_DEVICE_ACTION_SUCCESS,
    payload: {
        deviceId,
        ccActionCode
    }
});

const requestDeviceActionFailure = (deviceId, error, ccActionCode = "") => ({
    type: FETCH_DEVICE_ACTION_ERROR,
    payload: {
        deviceId,
        error,
        ccActionCode
    }
});

const requestSessionActionInProgress = sessionId => ({
    type: FETCH_SESSION_ACTION_IN_PROGRESS,
    payload: {
        sessionId
    }
});

const requestSessionActionSuccess = sessionId => ({
    type: FETCH_SESSION_ACTION_SUCCESS,
    payload: {
        sessionId
    }
});

const requestSessionActionFailure = (sessionId, error) => ({
    type: FETCH_SESSION_ACTION_ERROR,
    payload: {
        sessionId,
        error
    }
});

const setTrustDeviceCurrentStatus = enrol => ({
    type: FETCH_DEVICE_ENROLMENT_STATE,
    payload: { enrol }
});

const setEnrolProcessError = error => ({
    type: FETCH_DEVICE_ENROLMENT_ERROR,
    payload: { error }
});

const setEnrolProcessSuccess = success => ({
    type: FETCH_DEVICE_ENROLMENT_SUCCESS,
    payload: { success }
});

const setTrustDeviceDataToken = dataToken => ({
    type: FETCH_DEVICE_ENROLMENT_DATA_TOKEN,
    payload: { dataToken }
});

const setEnrolProcessDeviceInformation = device => ({
    type: FETCH_DEVICE_ENROLMENT_INFORMATION,
    payload: { device }
});

const setEnrolProcessInProgress = isFetching => ({
    type: FETCH_DEVICE_ENROLMENT_IN_PROGRESS,
    payload: { isFetching }
});

const setAgentProfileAuthorization = authorized => ({
    type: FETCH_AGENT_PROFILE_VALIDATE_SUCCESS,
    payload: { authorized }
});

const requestDevices = (
    blocked,
    forgotten,
    limit = DEVICES_TO_SHOW,
    isFetching
) => dispatch => {
    const query = {
        query: {
            blocked,
            limit,
            forgotten
        }
    };

    if (limit <= DEVICES_TO_SHOW)
        dispatch(requestDevicesInProgress(blocked, forgotten, isFetching));

    return APIGet(dispatch, apiURLBuilder.getURL("devices"), query).then(
        response => {
            const devices = response.map((d, i) =>
                Object.assign(d, {
                    alias:
                        d.alias ||
                        formatText("manageDevices-deviceWithoutAlias", [i]),
                    forgotten
                })
            );
            dispatch(requestDevicesSuccess(blocked, forgotten, devices));
        },
        error => {
            dispatch(requestDevicesFailure(blocked, forgotten, error));
        }
    );
};

const requestAllDevices = (isFetching, limit = DEVICES_TO_SHOW) => dispatch => {
    // Forgotten and Blocked Devices
    dispatch(requestDevices(true, true, limit, isFetching));
    // Forgotten Devices
    dispatch(requestDevices(true, false, limit, isFetching));
    // Blocked Devices
    dispatch(requestDevices(false, true, limit, isFetching));
    // Normal Devices
    dispatch(requestDevices(false, false, limit, isFetching));
};

const requestSessionsByDevice = deviceId => dispatch => {
    const startDate = moment()
        .subtract(30, "days")
        .format("YYYY-MM-DD");
    dispatch(requestSessionsByDeviceInProgress(deviceId));
    return APIGet(
        dispatch,
        apiURLBuilder.getURL("sessionsByDevice", {
            deviceId,
            startDate,
            limit: SESSIONS_LIMIT
        })
    ).then(
        response => {
            dispatch(requestSessionsByDeviceSuccess(deviceId, response));
        },
        error => {
            dispatch(requestSessionsByDeviceFailure(deviceId, error));
        }
    );
};

const requestDeviceInformation = deviceId => dispatch => {
    dispatch(setEnrolProcessInProgress(true));
    return APIGet(
        dispatch,
        apiURLBuilder.getURL("device", {
            deviceId
        })
    ).then(
        response => {
            dispatch(setEnrolProcessInProgress(false));
            dispatch(setTrustDeviceCurrentStatus(!response.trusted));
            dispatch(setEnrolProcessDeviceInformation(response));
        },
        error => {
            dispatch(setEnrolProcessInProgress(false));
            dispatch(setEnrolProcessError(error));
        }
    );
};

const requestSetAlias = (deviceId, alias, ccWrapUpCode) => dispatch => {
    const data = { query: { alias: alias } };
    dispatch(requestDeviceActionInProgress(deviceId));
    return APIPost(
        dispatch,
        apiURLBuilder.getURL("deviceSetAlias", { deviceId }),
        data
    ).then(
        () => {
            recordLink(DEVICES_EDIT_NAME, DEVICES_SUCCESS);

            dispatch(requestDeviceActionSuccess(deviceId, ccWrapUpCode));
            dispatch(requestAllDevices());
            dispatch(
                success({
                    message: formatText("manageDevices-notificationAliasSeted")
                })
            );
        },
        error => {
            recordLink(DEVICES_EDIT_NAME, DEVICES_ERROR);

            dispatch(requestDeviceActionFailure(deviceId, error, ccWrapUpCode));
        }
    );
};

const requestInvalidateSession = (deviceId, sessionId) => (
    dispatch,
    getState
) => {
    dispatch(requestSessionActionInProgress(sessionId));
    return APIPost(
        dispatch,
        apiURLBuilder.getURL("sessionInvalidate", { sessionId })
    ).then(
        () => {
            const sessions = getState()
                .devices.get("sessions")
                .get(deviceId);
            Array.isArray(sessions) &&
                dispatch(requestSessionsByDevice(deviceId));
            dispatch(requestSessionActionSuccess(sessionId));
            dispatch(requestAllDevices());
            dispatch(
                success({
                    message: formatText(
                        "manageDevices-notificationDeviceInvalidated"
                    )
                })
            );
        },
        error => {
            dispatch(requestSessionActionFailure(sessionId, error));
        }
    );
};

const requestBlockDevice = (deviceId, block, ccWrapUpAction) => dispatch => {
    const data = { query: { blocked: block } };
    dispatch(requestDeviceActionInProgress(deviceId));
    return APIPost(
        dispatch,
        apiURLBuilder.getURL("deviceBlock", { deviceId }),
        data
    ).then(
        () => {
            recordLink(DEVICES_BLOCK, DEVICES_SUCCESS);

            dispatch(requestDeviceActionSuccess(deviceId, ccWrapUpAction));
            dispatch(requestAllDevices());
            dispatch(
                success({
                    message: formatText(
                        block
                            ? "manageDevices-notificationDeviceBlocked"
                            : "manageDevices-notificationDeviceUnblocked"
                    )
                })
            );
        },
        error => {
            recordLink(DEVICES_BLOCK, DEVICES_ERROR);

            dispatch(
                requestDeviceActionFailure(deviceId, error, ccWrapUpAction)
            );
        }
    );
};

const requestForgetDevice = (
    deviceId,
    block,
    currentStatusForgotten,
    ccWrapUpAction
) => dispatch => {
    const forgottenNew = !currentStatusForgotten;
    const data = { query: { forgotten: forgottenNew } };
    dispatch(requestDeviceActionInProgress(deviceId));
    return APIPost(
        dispatch,
        apiURLBuilder.getURL("deviceForget", { deviceId }),
        data
    ).then(
        () => {
            recordLink(DEVICES_HIDE, DEVICES_SUCCESS);

            dispatch(requestDeviceActionSuccess(deviceId, ccWrapUpAction));
            dispatch(requestAllDevices());
            dispatch(
                success({
                    message: formatText(
                        forgottenNew
                            ? "manageDevices-notificationDeviceForgotten"
                            : "manageDevices-notificationDeviceRemember"
                    )
                })
            );
        },
        error => {
            recordLink(DEVICES_HIDE, DEVICES_ERROR);

            dispatch(
                requestDeviceActionFailure(deviceId, error, ccWrapUpAction)
            );
        }
    );
};

const removeSessionsByDevice = deviceId => dispatch => {
    dispatch(removeSessionsByDeviceAction(deviceId));
};

const clearDevices = () => dispatch => {
    dispatch(clearDevicesAction());
};

const toggleTrustDeviceStatus = (deviceId, trusted) => (dispatch, getState) => {
    const callback = (dispatch, getState, dataToken) => {
        const query = {
            query: Object.assign({ trusted }, trusted ? { dataToken } : {})
        };
        dispatch(setEnrolProcessInProgress(true));
        return APIPost(
            dispatch,
            apiURLBuilder.getURL("deviceTrust", { deviceId }),
            query
        )
            .then(() => dispatch(setEnrolProcessSuccess(true)))
            .catch(error => dispatch(setEnrolProcessError(error)));
    };
    return new Promise(function() {
        signaturePatternActions.validateAndExecute(
            dispatch,
            getState,
            callback
        );
    });
};

const validateAgentProfile = () => (dispatch) => {
    return APIGet({
        dispatch,
        endpoint: 'validateAgentProfile',
    }).then(response => {
        dispatch(setAgentProfileAuthorization(response.authorized));
    }).catch(() => dispatch(setAgentProfileAuthorization(false)));
}

module.exports = {
    requestSessionsByDevice,
    clearDevices,
    openModal,
    requestSetAlias,
    requestInvalidateSession,
    requestBlockDevice,
    requestForgetDevice,
    requestAllDevices,
    requestDevices,
    removeSessionsByDevice,
    requestDeviceInformation,
    toggleTrustDeviceStatus,
    setTrustDeviceCurrentStatus,
    setTrustDeviceDataToken,
    setEnrolProcessError,
    validateAgentProfile
};
